import React from 'react';
import { Theme, useTheme } from '@mui/material';
import type { CSSObject } from 'tss-react';

// Stand-ins for the parts of the legacy `@mui/styles` (JSS) API that tss-react does not provide.

/**
 * Types style rules for tss-react's `withStyles` so values like `position: 'relative'` keep their literal types.
 * Returns the rules unchanged.
 */
export const createStyles = <RuleName extends string>(styles: Record<RuleName, CSSObject>) => styles;

/** The `theme` prop that `withTheme` passes. */
export type WithTheme<T = Theme> = {
  theme: T;
};

/**
 * The `classes` prop of a component wrapped in tss-react's `withStyles(Component, styles)`. With `IncludeTheme`
 * it also declares the `theme` prop, which the component gets from `withTheme`.
 */
export type WithStyles<Styles, IncludeTheme extends boolean = false> = {
  classes?: Partial<Record<keyof (Styles extends (...args: any[]) => infer Rules ? Rules : Styles), string>>;
} & (IncludeTheme extends true ? WithTheme : unknown);

/** Passes the Material UI theme as the `theme` prop, for class components that cannot call `useTheme`. */
export const withTheme = <C extends React.ComponentType<any>>(Component: C) => {
  const WithThemeComponent = (props: Omit<React.ComponentProps<C>, 'theme'>) => {
    const theme = useTheme();
    const Wrapped = Component as React.ComponentType<any>;
    return <Wrapped {...props} theme={theme} />;
  };
  return WithThemeComponent;
};
